<template>
    <div class="app-asset-manager--translations app-asset-manager grid">

        <div class="app-toast is--error is--inline col-12" role="alert" v-if="errors">
            <div class="app-toast__text">
              <p v-for="(error, prop) in errors" :key="`error-${prop}`" class="app-toast__title">{{ error[0] }}</p>
            </div>
            <button class="app-toast__close element-button--icon element-button" type="button" data-bs-dismiss="alert" aria-label="Close" @click="errors = false">
                <svg aria-hidden="true" class="element-icon--x element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
            </button>
        </div>

        <div v-show="status"
            :class="['status', { 'status--show': status }]">
            <div class="status-message">{{ status }}</div>
            <div v-show="uploadPercentage"
                class="element-progress">
                <p class="element-progress__text">{{ uploadPercentage }}%</p>
                <div class="element-progress__bar">
                    <div
                        class="element-progress__bar-filled"
                        role="progressbar"
                        aria-valuenow="0"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :style="`width: ${uploadPercentage}%;`">
                    </div>
                </div>
            </div>
        </div>

        <div class="app-asset-manager__sidebar app-vertical-navigation col-12 col-sm-6 col-md-5 col-lg-3">
            <div class="app-vertical-navigation__header">
                <h3 class="app-vertical-navigation__header-title">Asset Library</h3>
            </div>
            <div class="app-vertical-navigation__menu-item is--static">
                <svg class="element-icon--search element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-search"></use></svg>
                <h4 class="app-vertical-navigation__menu-item-title">View</h4>
            </div>
            <div class="app-vertical-navigation__submenu-item is--static">
                <div class="app-search">
                    <div class="input text">
                        <input v-model="filter"
                            type="search"
                            placeholder="Search by name...">
                    </div>
                </div>
            </div>
            <div v-if="types.length && !mode" class="app-vertical-navigation__submenu-item is--static">
                <div class="app-asset-manager__media-types">
                    <label v-for="type in types"
                        :key="`filetype-${type}`"
                        :title="type"
                        class="app-asset-manager__media-type">
                        <div class="input checkbox">
                            <input v-model="selectedTypes"
                                type="checkbox"
                                :value="type"
                                autocomplete="off"
                                :disabled="onlySelectedFilter(type)">
                        </div>
                        <span class="app-asset-manager__media-type-info">
                            <svg :class="['element-icon', 'element-icon--asset-' + type]" width="1em" height="1em"><use :href="['/img/icons.svg#icon-asset-' + type]"></use></svg>
                            {{ type }}
                        </span>
                    </label>
                </div>
            </div>
            <div v-if="!mode && showAssignmentFilters" class="app-vertical-navigation__submenu-item is--static">
                <div class="element-button-group">
                    <button v-for="type in assignmentTypes"
                        :key="`assignment-${type.label}`"
                        type="button"
                        :class="['element-button-group__button', { 'is--active' : selectedAssignment === type.value }]"
                        @click="selectedAssignment = type.value">
                        {{ type.label }}
                    </button>
                </div>
            </div>
            <asset-manager-folder
                class="root-tree"
                :folder="root"
                :collapsed="false">
            </asset-manager-folder>
            <div v-if="!loaded"
                class="text-muted">
                Loading, please wait...
            </div>
            <p v-else-if="!filteredFiles.length"
                class="app-asset-manager__empty element-empty-instructions">
                No assets found.
            </p>
        </div>

        <div class="app-asset-manager__preview col-12 col-sm-6 col-md-7 col-lg-9">
            <div class="app-asset-manager__preview-english">
                <div class="app-asset-manager__preview-header">
                    <h3 class="app-asset-manager__preview-title">English Version</h3>
                    <div class="app-asset-manager__preview-header-actions" v-if="file">
                        <div v-if="metadata.supportsCaptions && !selectMode && metadata.captions_url">
                            <a
                                :title="metadata.captions_url"
                                :href="metadata.captions_url"
                                target="_blank"
                                class="element-button--secondary element-button">
                                Download Captions
                            </a>
                        </div>
                    </div>
                </div>
                <div v-if="file" 
                    :key="`${file.id}-${file.timestamp}`"
                    class="app-asset-manager__preview-body">
                    <div class="app-asset-manager__preview-file">
                        <div class="input text">
                            <label>Filename</label>
                            <input
                                type="text"
                                spellcheck="false"
                                disabled
                                :value="filename">
                        </div>
                        <span class="app-asset-manager__preview-file-extension">.{{ extension }}</span>
                    </div>

                    <div class="app-asset-manager__preview-asset">
                        <img v-if="file.type === 'image'"
                            :title="file.url"
                            :src="fileUrl">
                        <audio v-else-if="file.type === 'audio'"
                            controls
                            :title="file.url"
                            :src="fileUrl">
                        </audio>
                        <video v-else-if="file.type === 'video'"
                            controls
                            :title="file.url"
                            :src="fileUrl">
                            <track v-if="metadata.captions"
                                default
                                srclang="en"
                                :src="`/assets/${metadata.captions}`"
                            />
                        </video>
                        <div v-else
                            class="app-asset-manager__preview-asset-unavailable">
                            <p>Preview not available for {{ file.type }} files.</p>
                            <a
                                :title="file.url"
                                :href="file.url"
                                target="_blank"
                                class="element-button--secondary element-button">
                                Open File
                            </a>
                        </div>
                    </div>

                    <div class="app-asset-manager__preview-metadata">
                        <h4 class="page-main__section-subtitle">Metadata</h4>
                        <p>
                            <strong>Asset Type</strong>
                            <app-loader-fill v-if="!metadata.mimeType" />
                            <span v-else>{{ metadata.mimeType }}</span>
                        </p>
                        <p>
                            <strong>Last Modified</strong>
                            <app-loader-fill v-if="!metadata.updatedAtFormatted" />
                            <span v-else>{{ metadata.updatedAtFormatted }}</span>
                        </p>
                        <p v-if="metadata.uploadUser">
                            <strong>Modified By</strong>
                            {{ metadata.uploadUser }}
                        </p>
                        <!-- <p>
                            <strong>Raw filename:</strong>
                            <app-loader-fill v-if="!metadata.rawFilename" />
                            <span v-else>{{ metadata.rawFilename }}</span>
                        </p> -->
                        <p>
                            <strong>File Size</strong>
                            <app-loader-fill v-if="!metadata.size" />
                            <span v-else>{{ metadata.size }}</span>
                            <strong v-show="metadata.optimized" class="element-badge is--finished">Optimized</strong>
                        </p>
                        <p v-if="metadata.hasDimensions">
                            <strong>File Dimensions</strong> 
                            <app-loader-fill v-if="!metadata.dimensions" />
                            <span v-else>{{ metadata.dimensions.width }} x {{ metadata.dimensions.height }}</span>
                        </p>
                        <p v-if="metadata.hasDuration">
                            <strong>File Duration</strong>
                            <app-loader-fill v-if="!metadata.duration" />
                            <span v-else>{{ metadata.durationFormatted }}</span>
                        </p>
                    </div>
                </div>
                <div v-else class="app-asset-manager__preview-body-empty element-empty-instructions">
                    <img class="app-asset-manager__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
                    <p>Select a file to preview.</p>
                </div>
            </div>
            <div class="app-asset-manager__preview-translated">
                <div class="app-asset-manager__preview-header">
                    <h3 class="app-asset-manager__preview-title">{{ locale.language_code }} Version</h3>
                    <div v-if="translatedFile && file" class="app-asset-manager__preview-header-actions">
                        <!-- Consolidated Captions Dropdown -->
                        <b-dropdown text="Captions" class="element-button--secondary-dropdown-captions element-button--secondary-dropdown" v-if="translatedMetadata.supportsCaptions && !selectMode">
                            <b-dropdown-item>
                                <file-uploader
                                    :url="`/media/${translatedFile.id}/edit`"
                                    name="captions"
                                    :button-label="translatedMetadata.captions ? 'Replace Captions' : 'Upload Captions'"
                                    :valid-extensions="['.vtt']"
                                    @start="onFileUploadStart"
                                    @progress="onFileUploadProgress"
                                    @success="onFileUploadSuccess"
                                    @fail="onFileUploadFail">
                                </file-uploader>
                            </b-dropdown-item>
                            <template v-if="translatedMetadata.captions_url">
                                <b-dropdown-item>
                                    <a
                                        title="Download Captions"
                                        :href="`/media/${translatedFile.id}/captions/download`"
                                        target="_blank">
                                        <svg class="element-icon--download element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-download"></use></svg>
                                        Download Captions
                                    </a>
                                </b-dropdown-item>
                                <b-dropdown-item v-if="translatedMetadata.captions_url">
                                    <button
                                        title="Delete Captions"
                                        @click="deleteCaptions(`${translatedFile.id}`)"
                                        class="element-button is--alert">
                                        <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                        Delete Captions
                                    </button>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>
                        <file-uploader
                            class="ml-2"
                            :url="`/media/${translatedFile.id}/edit`"
                            button-label="Replace Asset"
                            button-class="element-button--primary element-button"
                            :valid-extensions="['.'+extension]"
                            @start="onFileUploadStart"
                            @progress="onFileUploadProgress"
                            @success="onFileUploadSuccess"
                            @fail="onFileUploadFail">
                        </file-uploader>
                        <button
                            class="element-button--icon element-button is--alert"
                            title="Delete Asset"
                            @click="deleteFile(translatedFile)">
                            <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                        </button>
                    </div>
                    <div class="app-asset-manager__preview-header-actions" v-else-if="!translatedFile && file">
                        <file-uploader
                            :url="`/media/${file.id}/translation`"
                            name="asset"
                            button-class="element-button--primary element-button"
                            button-label="Upload Translation"
                            :options="{'locale':locale.locale}"
                            :valid-extensions="['.'+extension]"
                            @start="onFileUploadStart"
                            @progress="onFileUploadProgress"
                            @success="onFileUploadSuccess"
                            @fail="onFileUploadFail">
                        </file-uploader>
                    </div>
                </div>
                <div v-if="translatedFile"
                    class="app-asset-manager__preview-body"
                    :key="`${translatedFile.id}-${translatedFile.timestamp}`">
                    <div class="app-asset-manager__preview-file">
                        <div class="input text">
                            <label>Filename</label>
                            <input
                                type="text"
                                spellcheck="false"
                                :disabled="filesLoading[translatedFile.id]"
                                :value="translatedFilename"
                                @change="updateFilename($event)">
                        </div>
                        <span class="app-asset-manager__preview-file-extension">.{{ extension }}</span>
                    </div>

                    <div class="app-asset-manager__preview-asset">
                        <img v-if="translatedFile.type === 'image'"
                            :title="translatedFile.url"
                            :src="translatedFileUrl">
                        <audio v-else-if="translatedFile.type === 'audio'"
                            controls
                            :title="translatedFile.url"
                            :src="translatedFileUrl">
                        </audio>
                        <video v-else-if="translatedFile.type === 'video'"
                            controls
                            :title="translatedFile.url"
                            :src="translatedFileUrl">
                            <track v-if="translatedMetadata.captions"
                                default
                                srclang="en"
                                :src="`/assets/${translatedMetadata.captions}`"
                            />
                        </video>
                        <div v-else
                            class="app-asset-manager__preview-asset-unavailable">
                            <p>Preview not available for {{ translatedFile.type }} files.</p>
                            <a
                                :title="translatedFile.url"
                                :href="translatedFile.url"
                                target="_blank"
                                class="element-button--secondary element-button">
                                Open File
                            </a>
                        </div>
                    </div>

                    <div class="app-asset-manager__preview-metadata">
                        <h4 class="page-main__section-subtitle">Metadata</h4>
                        <p>
                            <strong>Asset Type</strong>
                            <app-loader-fill v-if="!translatedMetadata.mimeType" />
                            <span v-else>{{ translatedMetadata.mimeType }}</span>
                        </p>
                        <p>
                            <strong>Last Modified</strong>
                            <app-loader-fill v-if="!translatedMetadata.updatedAtFormatted" />
                            <span v-else>{{ translatedMetadata.updatedAtFormatted }}</span>
                        </p>
                        <p v-if="translatedMetadata.createdAtFormatted">
                            <strong>Modified By</strong>
                            {{ translatedMetadata.uploadUser }}
                        </p>
                        <!-- <p>
                            <strong>Raw filename:</strong>
                            <app-loader-fill v-if="!translatedMetadata.rawFilename" />
                            <span v-else>{{ translatedMetadata.rawFilename }}</span>
                        </p> -->
                        <p>
                            <strong>File Size</strong>
                            <app-loader-fill v-if="!translatedMetadata.size" />
                            <span v-else>{{ translatedMetadata.size }}</span>
                            <strong v-show="translatedMetadata.optimized" class="element-badge is--finished">Optimized</strong>
                        </p>
                        <p v-if="translatedMetadata.hasDimensions">
                            <strong>File Dimensions</strong> 
                            <app-loader-fill v-if="!translatedMetadata.dimensions" />
                            <span v-else>{{ translatedMetadata.dimensions.width }} x {{ translatedMetadata.dimensions.height }}</span>
                        </p>
                        <p v-if="translatedMetadata.hasDuration">
                            <strong>File Duration</strong>
                            <app-loader-fill v-if="!translatedMetadata.duration" />
                            <span v-else>{{ translatedMetadata.durationFormatted }}</span>
                        </p>
                    </div>
                </div>
                <div v-else class="app-asset-manager__preview-body-empty element-empty-instructions">
                    <div v-if="file">
                        <img class="app-asset-manager__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
                        <p>Translated asset has not been uploaded.</p>
                    </div>
                    <div v-else class="app-asset-manager__preview-body-empty element-empty-instructions">
                        <img class="app-asset-manager__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
                        <p>Select a file to preview.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import FileUploader from './FileUploader.vue'
import AssetManagerFolder from './AssetManagerFolder.vue'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import api from '../api/assets'

// styles
import 'filepond/dist/filepond.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// Create FilePond component
const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview
)

export default {
    components: {
        AssetManagerFolder,
        FileUploader,
        FilePond
    },
    props: {
        files: {
            type: Array,
            default: () => []
        },
        locale: {
            type: Object
        },
        // translatedFiles: {
        //     type: Array,
        //     default: () => []
        // },
        maxFileSize: {
            type: Number,
            default: (50 * 1024 * 1024) // 50MB
        },
        source: {
            type: String
        },
        destination: {
            type: String
        },
        selectMode: {
            type: Boolean,
            default: false
        },
        validExtensions: {
            type: Object,
            default: () => { }
        },
        validMimeTypes: {
            type: Object,
            default: () => { }
        },
        showAssignmentFilters: {
            type: Boolean,
            default: true
        },
        autoLoad: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            selectedTypes: _.keys(this.validExtensions),
            assignmentTypes: [{
                label: 'All',
                value: null
            }, {
                label: 'Assigned',
                value: true
            }, {
                label: 'Unassigned',
                value: false
            }],
            selectedAssignment: null,
            filter: '',
            status: null,
            errors: false,
            uploadPercentage: 0
        }
    },
    computed: {
        ...mapState('assets', {
            ready: state => state.ready,
            loaded: state => state.loaded,
            options: state => state.options,
            selectedId: state => state.selectedId,
            filesLoading: state => state.filesLoading
        }),
        ...mapGetters('assets', {
            allFiles: 'fileSet',
            file: 'selectedFile',
            metadata: 'selectedMetadata',
            translatedFile: 'selectedTranslatedFile',
            translatedMetadata: 'selectedTranslatedMetadata'
        }),
        serverConfig () {
            return {
                process: this.uploadUrl,
                fetch: null,
                revert: null,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.$store.state.csrfToken
                }
            }
        },
        uploadUrl () {
            return this.destination || this.source
        },
        mode () {
            return _.get(this.options, 'assetType')
        },
        filename () {
            return this.file?.name.replace(/\.[^/.]+$/, '')
        },
        translatedFilename () {
            return this.translatedFile?.name.replace(/\.[^/.]+$/, '')
        },
        fileUrl () {
            const timestamp = _.get(this.file, 'timestamp', 0)
            return `${this.file?.url}?t=${timestamp}`
        },
        translatedFileUrl () {
            const timestamp = _.get(this.translatedFile, 'timestamp', 0)
            return `${this.translatedFile?.url}?t=${timestamp}`
        },
        extension () {
            return /(?:\.([^.]+))?$/.exec(this.file?.name)[1]
        },
        extensions () {
            if (this.mode) {
                return this.getExtensions(this.mode)
            }
    
            const list = _.reduce(this.validExtensions, (result, extensions) => {
                const formattedExt = _.map(extensions, (ext) => `.${ext}`)
                return result.concat(formattedExt)
            }, [])

            return _.uniq(list)
        },
        mimeTypes () {
            if (this.mode) {
                let _result = [];
                if (_.isArray(this.mode))
                {
                    const _all = this.validMimeTypes;
                    _(this.mode).each(function(_type) {
                        _result = _.union(_result, _.get(_all, _type, []))
                    });
                    return _result
                }
                else
                {
                    return _.get(this.validMimeTypes, this.mode, [])
                }
            }
    
            return _.reduce(this.validMimeTypes, (result, types) => result.concat(types), [])
        },
        filteredFiles () {
            let filtered = this.allFiles

            const typeFilters = !this.mode && this.selectedTypes.length ? this.selectedTypes : this.types

            // filter by file types
            filtered = _.filter(filtered, ({ type }) => {
                return typeFilters.indexOf(type) >= 0
            })

            // filter by assignment
            if (this.selectedAssignment !== null) {
                filtered = _.filter(filtered, ({ data_objects = [] }) => {
                    return _.xor([!!data_objects.length, this.selectedAssignment]).length === 1
                })
            }

            if (this.filter.length) {
                filtered = _.filter(filtered, ({ name }) => {
                    return name.toLowerCase().indexOf(this.filter.toLowerCase()) >= 0 
                })
            }

            return filtered
        },
        types () {
            const types = this.mode ? (_.isArray(this.mode) ? this.mode : [this.mode]) : _.keys(this.validExtensions)
            return types.sort()
        },
        root () {
            return { files: this.filteredFiles }
        }
    },
    methods: {
        handleFilePondInit () {
            // console.log('FilePond has initialized')
        },
        validateFileType: (source, type) => new Promise((resolve) => {
            if (source.name.endsWith('woff')) {
                return resolve('font/woff')
            } else if (source.name.endsWith('woff2')) {
                return resolve('font/woff2')
            }

            resolve(type)
        }),
        selectFile () {
            this.$emit('file-selected', this.file)
        },
        getIcon (type) {
            let icon = type
            switch (type) {
                case 'image':
                case 'audio':
                case 'video':
                case 'font':
                    break
                default:
                    icon = 'document'
                    break
            }

            return icon
        },
        getExtensions (type) {
            let extensions = [];
            if (_.isArray(type))
            {
                const _all = this.validExtensions;
                _(type).each(function(_type) {
                    extensions = _.union(extensions, _.get(_all, _type))
                });
            }
            else
            {
                extensions = _.get(this.validExtensions, type)
            }

            return _.map(extensions, (ext) => `.${ext}`)
        },
        onlySelectedFilter (type) {
            return this.selectedTypes.length === 1 && this.selectedTypes.indexOf(type) >= 0
        },
        setStatus (message, progress = 0) {
            this.status = message
            this.uploadPercentage = progress
        },
        onFileUploadStart () {
            this.setStatus('Starting file upload...')
            this.errors = false
        },
        onFileUploadProgress ({ loaded, total }) {
            this.setStatus('Uploading...', Math.round(loaded / total * 100))
        },
        onFileUploadFail (errors) {
            console.log(errors)
            this.errors = errors

            this.setStatus(null)
        },
        async onFileUploadSuccess (file) {
            // select file
            if (file) {
                this.$store.commit('assets/updateTranslatedFile', file)
                await this.$store.dispatch('assets/refreshMetadata', file.id)
            }

            this.setStatus(null)
        },
        async updateFilename (e) {
            let newName = e.target.textContent || e.target.innerText || e.target.value
            if (!newName.length) {
                newName = this.translatedFile.name
            }
            const name = `${newName}.${this.extension}`
            if (name !== this.translatedFile.name) {
                this.translatedFile.name = name
                await this.$store.dispatch('assets/updateTranslatedFile', { id: this.translatedFile.id, name })
            }
        },
        async deleteFile (file) {
            if (!confirm(`Are you sure you want to delete [${file.name}]?`)) {
                return
            }
            
            this.setStatus(`Deleting [${file.name}]`)

            await this.$store.dispatch('assets/deleteFile', file.id)
            this.$store.commit('assets/removeTranslatedFile', file.id)

            this.setStatus(null)
        },
        async deleteCaptions(mediaId) {
            if (!confirm(`Are you sure you want to delete captions?`)) {
                return
            }
            try {
                const response = await api.deleteCaptions(mediaId);

                if (response)
                    this.onFileUploadSuccess(response);

            } catch (e) {
                alert('Could not delete captions.')
            }
        },
    },
    async created () {
        this.$store.commit('assets/setSource', this.source)
        this.$store.commit('assets/setLoadTranslations', true)
        this.$store.commit('assets/setFiles', this.files)
        this.$store.commit('assets/setTranslatedFiles', this.locale?.translatedAssetList ?? [])

        if (this.autoLoad) {
            if (!this.files.length) {
                await this.$store.dispatch('assets/update')
            } else {
                this.$store.commit('assets/setLoaded', true)
            }
        }

        // check for initial selection (if not select mode)
        if (!this.selectMode) {
            const hash = window.location.hash.substr(1)
            if (hash) {
                this.$store.dispatch('assets/selectFile', parseInt(hash, 10))
            }
        }

        this.$store.commit('assets/setReady', true)
    }
}
</script>
